import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useLayout } from "../../../contexts/LayoutContext";
import { resolveFeatureSections } from "../../../utils/featureSections";
import { 
  ExternalLink,
  MapPin,
  Phone,
  Mail,
  ChevronRight,
  GraduationCap, 
  MessageCircle,
  Briefcase, 
  User
} from "lucide-react";
import logo from "../../../assets/logo/logo.png";
import "./Footer.css";

export default function Footer() {
  const { lang } = useLanguage();
  const { settings } = useLayout();
  const isAr = lang === "ar";
  const sections = resolveFeatureSections(settings);

  const aboutLinks = [
    { label: isAr ? 'من نحن' : 'About Us', path: '/about' },
    { label: isAr ? 'خدماتنا' : 'Our Services', path: '/services' },
    { label: isAr ? 'تواصل معنا' : 'Contact', path: '/contact' },
  ];

  const servicesLinks = [
    { label: isAr ? 'الكورسات' : 'Courses', path: '/courses', icon: GraduationCap },
    { label: isAr ? 'الاستشارات' : 'Consulting', path: '/consulting', icon: MessageCircle },
    { label: isAr ? 'الوظائف' : 'Jobs', path: '/jobs', icon: Briefcase },
    { label: isAr ? 'المستقلون' : 'Freelancers', path: '/freelancers', icon: User },
  ].filter((item) => item.enabled !== false);

  const legalLinks = [
    { label: isAr ? 'سياسة الخصوصية' : 'Privacy', path: '/privacy-policy' },
    { label: isAr ? 'الشروط والأحكام' : 'Terms', path: '/terms' },
    { label: isAr ? 'الشحن' : 'Shipping', path: '/shipping-policy' },
    { label: isAr ? 'الاسترجاع' : 'Returns', path: '/refund-policy' },
  ];

  return (
    <footer className="footer footer--modern" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="footer__bg">
        <div className="footer__glow-orb footer__glow-orb--1" />
        <div className="footer__glow-orb footer__glow-orb--2" />
        <div className="footer__glow-orb footer__glow-orb--3" />
      </div>

      <div className="footer__container">
        <div className="footer__main">
          
          {/* العمود 1: البراند */}
          <div className="footer__col footer__col--brand">
            <div className="footer__brand">
              <div className="footer__logo-box">
                <img src={logo} alt="Mousa Group" className="footer__logo-img" />
                <div className="footer__logo-shine" />
              </div>
              
              <h2 className="footer__brand-name">Mousa Group</h2>
              
              <p className="footer__brand-desc">
                {isAr 
                  ? "شركة متخصصة في الحلول الرقمية والتقنية. نقدم منتجات، كورسات، استشارات، وفرص عمل في مجال التكنولوجيا."
                  : "A company specialized in digital and technology solutions. We offer products, courses, consulting, and tech career opportunities."}
              </p>

              <a 
                href="https://www.linkedin.com/company/mousa-group" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer__linkedin-btn"
              >
                <span>LinkedIn</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* العمود 2: About */}
          <div className="footer__col">
            <h3 className="footer__heading">
              <span className="footer__heading-text">{isAr ? 'عن الشركة' : ' About Us'}</span>
              <span className="footer__heading-line" />
            </h3>
            <ul className="footer__menu">
              {aboutLinks.map((item, idx) => (
                <li key={idx}>
                  <Link to={item.path} className="footer__link">
                    <ChevronRight size={16} className="footer__link-arrow" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* العمود 3: Services */}
          <div className="footer__col">
            <h3 className="footer__heading">
              <span className="footer__heading-text">{isAr ? 'خدماتنا' : 'Services'}</span>
              <span className="footer__heading-line" />
            </h3>
            <ul className="footer__menu">
              {servicesLinks.map((item, idx) => (
                <li key={idx}>
                  <Link to={item.path} className="footer__link footer__link--with-icon">
                    <item.icon size={16} className="footer__link-icon" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* العمود 4: Legal */}
          <div className="footer__col">
            <h3 className="footer__heading">
              <span className="footer__heading-text">{isAr ? 'سياساتنا' : 'Policies'}</span>
              <span className="footer__heading-line" />
            </h3>
            <ul className="footer__menu">
              {legalLinks.map((item, idx) => (
                <li key={idx}>
                  <Link to={item.path} className="footer__link">
                    <ChevronRight size={16} className="footer__link-arrow" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* العمود 5: Contact */}
          <div className="footer__col">
            <h3 className="footer__heading">
              <span className="footer__heading-text">{isAr ? 'اتصل بنا' : 'Contact Us'}</span>
              <span className="footer__heading-line" />
            </h3>
            
            <div className="footer__contact-list">
              <a href="mailto:support@mousagroup.net" className="footer__contact-item">
                <span className="footer__contact-icon">
                  <Mail size={18} />
                </span>
                <span className="footer__contact-text">support@mousagroup.net</span>
              </a>

              <a href="tel:+13079988849" className="footer__contact-item">
                <span className="footer__contact-icon">
                  <Phone size={18} />
                </span>
                <span className="footer__contact-text" dir="ltr">+1 (307) 998-8849</span>
              </a>

              <div className="footer__contact-item">
                <span className="footer__contact-icon">
                  <MapPin size={18} />
                </span>
                <span className="footer__contact-text">
                  30 N Gould St, Sheridan, WY 82801
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer__separator">
          <div className="footer__separator-glow" />
        </div>

        <div className="footer__copyright-bar">
          <p className="footer__copyright-text">
            © {new Date().getFullYear()} 
            <span className="footer__copyright-brand"> Mousa Group LLC</span>
            <span className="footer__copyright-dot"> • </span>
            {isAr ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
          </p>
        </div>
      </div>
    </footer>
  );
}
